import { z } from "zod";

/**
 * Package Type enum for form-only use
 * Used to determine which fields to show in the form
 */
export const PackageType = {
  DEFAULT: "DEFAULT",
  PRODUCT: "PRODUCT",
} as const;

export type PackageTypeValue = (typeof PackageType)[keyof typeof PackageType];

export const PackageFormSchema = z.object({
  type: z.enum([PackageType.DEFAULT, PackageType.PRODUCT]),

  name: z.string().min(1, "Name is required").max(255, "Name must be 255 characters or less"),

  description: z.string().max(1000, "Description must be 1000 characters or less").optional(),

  productCode: z
    .string()
    .min(1, "Product code is required")
    .max(50, "Product code must be 50 characters or less")
    .regex(
      /^[A-Z0-9_-]+$/i,
      "Product code must contain only letters, numbers, hyphens, and underscores",
    ),

  metadataQuantities: z.record(z.string(), z.number().int().positive()).nullable().optional(),

  featureCodes: z
    .array(z.string().min(1, "Feature code cannot be empty"))
    .min(1, "At least one feature is required"),

  isActive: z.boolean().default(true),

  defaultSiteStructureId: z.string().nullable().optional(),
});

export type PackageFormData = z.infer<typeof PackageFormSchema>;

export function toApiPackageRequest(data: PackageFormData): {
  productCode: string;
  type: "product" | "default";
  name: string;
  description?: string | null;
  featureCodes: string[];
  metadataQuantities?: Record<string, number> | null;
  isActive: boolean;
  defaultSiteStructureId?: string | null;
} {
  const request: {
    productCode: string;
    type: "product" | "default";
    name: string;
    description?: string | null;
    featureCodes: string[];
    metadataQuantities?: Record<string, number> | null;
    isActive: boolean;
    defaultSiteStructureId?: string | null;
  } = {
    productCode: data.productCode,
    type: data.type === PackageType.PRODUCT ? "product" : "default",
    name: data.name,
    featureCodes: data.featureCodes,
    isActive: data.isActive,
  };

  if (data.description !== undefined) {
    request.description = data.description || null;
  }

  if (data.metadataQuantities !== undefined) {
    request.metadataQuantities = data.metadataQuantities;
  }

  if (data.defaultSiteStructureId !== undefined) {
    request.defaultSiteStructureId = data.defaultSiteStructureId || null;
  }

  return request;
}

/**
 * Helper to detect package type from productCode
 * Used when editing existing packages
 */
export function detectPackageType(productCode: string): PackageTypeValue {
  return productCode.includes("DEFAULT") ? PackageType.DEFAULT : PackageType.PRODUCT;
}
